import { useContext, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Tab,
  Tabs,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Settings from '@mui/icons-material/Settings'
import BarChart from '@mui/icons-material/BarChart'
import EventNote from '@mui/icons-material/EventNote'
import { MentorLocalizationContext } from 'src/mentor/context/MentorLocalizationContext'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getImageLink, logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { HeaderContainer, HeaderGreeting } from '../MentorHeader/MentorHeaderStyles'
import PortalSwitcherButton from '../MentorHeader/PortalSwitcherButton'
import ClinicOwnerReport from '../ClinicOwnerReport/ClinicOwnerReport'
import { HomePageContainer, HomePageItem } from '../MentorHomePage/MentorHomepageStyles'

// Styled Tabs matching mentor portal theme - responsive
const MentorTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  backgroundColor: 'rgba(16, 185, 129, 0.04)',
  borderRadius: '8px',
  padding: isMobile ? '2px' : '4px',
  minHeight: 'auto',
  width: '100%',
  overflowX: isMobile ? 'auto' : 'visible',
  display: 'flex',
  visibility: 'visible',
  '& .MuiTabs-indicator': {
    backgroundColor: 'rgba(16, 185, 129, 0.95)',
    height: '3px',
    borderRadius: '3px',
  },
  '& .MuiTabs-flexContainer': {
    gap: isMobile ? '2px' : '4px',
    overflowX: isMobile ? 'auto' : 'visible',
    display: 'flex',
    width: '100%',
    justifyContent: isMobile ? 'flex-start' : 'space-between',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    order: 3,
  },
}))

const MentorTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'iconOnly',
})(({ theme, isMobile, iconOnly }) => ({
    textTransform: 'none',
    minWidth: iconOnly ? '48px' : isMobile ? '120px' : 'auto',
    flex: iconOnly ? '0 0 auto' : isMobile ? '0 1 auto' : '1 1 0',
    maxWidth: iconOnly ? '48px' : 'none',
    fontWeight: theme.typography.fontWeightRegular,
    padding: iconOnly ? '8px' : isMobile ? '8px 12px' : '10px 16px',
    color: 'rgba(71, 85, 105, 0.85)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontSize: isMobile ? '12px' : '14px',
    fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    whiteSpace: 'nowrap',
    display: 'flex !important',
    visibility: 'visible !important',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    '& .MuiTab-iconWrapper': {
      marginRight: iconOnly ? 0 : '8px',
      marginBottom: iconOnly ? 0 : '0px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Only apply icon-only styles when iconOnly is explicitly true
    ...(iconOnly === true && {
      // Hide label text spans, but keep icon wrapper visible
      '& > span:not(.MuiTab-iconWrapper)': {
        display: 'none !important',
      },
      '& .MuiTab-iconWrapper': {
        display: 'flex !important',
        marginRight: '0 !important',
      },
    }),
    '&:hover': {
      color: 'rgba(16, 185, 129, 0.95)',
      backgroundColor: 'rgba(16, 185, 129, 0.08)',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: 'rgba(16, 185, 129, 0.95)',
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(16, 185, 129, 0.12)',
    },
  }),
)

export default function PreferencesAndSettingsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const localizedData = useContext(MentorLocalizationContext)
  const { mentorProfileData, allLegalPolicies, mentorClinicAssociationData, portalViewSettings } =
    useContext(MentorProfileContext)
  const offersLive = mentorProfileData?.services?.live

  const showPayoutReports = mentorProfileData?.teacherUnEditableFields?.showPayoutReports
  const showEngagementModels = mentorProfileData?.teacherUnEditableFields?.showFeeModels

  const unAcceptedAgreements = allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length
  const translatedStrings = localizedData?.localizedData?.strings?.homePage

  const hidePayoutReports = false

  // Determine active tab based on current route
  const currentTab = location.pathname.includes('preferences-and-settings')
    ? 'preferences'
    : location.pathname.includes('manage-my-bookings')
    ? 'manage-bookings'
    : location.pathname.includes('stats')
    ? 'stats'
    : 'bookings'

  const handleTabChange = (event, newValue) => {
    if (newValue === 'bookings') {
      navigate('/mentor-home/my-clinic-associations')
    } else if (newValue === 'preferences') {
      navigate('/mentor-home/preferences-and-settings')
    } else if (newValue === 'stats') {
      navigate('/mentor-home/stats')
    } else if (newValue === 'manage-bookings') {
      navigate('/mentor-home/manage-my-bookings')
    }
  }

  // Profile dropdown state
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  console.log({ mentorProfileData })

  return (
    <>
      {/* Title and Profile/Portal Switcher Row */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <HeaderContainer className="hide-on-print-only">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              width: '100%',
              py: 1.5,
              gap: 2,
            }}
          >
            {/* Left Column - Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <HeaderGreeting
                main
                sx={{
                  fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
                  fontWeight: 600,
                }}
              >
                {currentTab === 'bookings'
                  ? 'Clinic Check-In & Bookings'
                  : currentTab === 'manage-bookings'
                  ? 'Manage Bookings'
                  : currentTab === 'stats'
                  ? 'Stats'
                  : 'Preferences & Settings'}
              </HeaderGreeting>
            </Box>
            {/* Right Column - Buttons (only as wide as needed) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 1 : 1.5,
                flexShrink: 0,
                justifyContent: 'flex-end',
              }}
            >
              <PortalSwitcherButton />
              {isMobile ? (
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    padding: '4px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    backgroundColor: 'rgba(16, 185, 129, 0.04)',
                  }}
                >
                  <Avatar
                    src={getImageLink(mentorProfileData?.profilePicId)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </IconButton>
              ) : (
                <Tooltip title="Account Menu" arrow>
                  <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{
                      padding: '6px',
                      border: '2px solid rgba(16, 185, 129, 0.2)',
                      backgroundColor: 'rgba(16, 185, 129, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Avatar
                      src={getImageLink(mentorProfileData?.profilePicId)}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </HeaderContainer>
      </Box>

      {/* Tabs Row - Full Width with Proper Spacing */}
      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          top: isMobile ? '56px' : '64px',
          zIndex: 1000,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MentorTabs value={currentTab} onChange={handleTabChange} isMobile={isMobile}>
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="bookings"
              icon={isMobile ? <CalendarMonth /> : undefined}
              label={isMobile ? 'Check-In' : isTablet ? 'Check-In' : 'Clinic Check-In & Bookings'}
            />
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="manage-bookings"
              icon={isMobile ? <EventNote /> : undefined}
              label={isMobile ? 'Bookings' : isTablet ? 'Bookings' : 'Manage Bookings'}
            />
            {getAllRolesInBoolean().isClinicOwner && (
              <MentorTab
                isMobile={isMobile}
                iconOnly={isMobile}
                value="stats"
                icon={isMobile ? <BarChart /> : undefined}
                label={isMobile ? 'Stats' : 'Stats'}
              />
            )}
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="preferences"
              icon={isMobile ? <Settings /> : undefined}
              label={isMobile ? 'Settings' : isTablet ? 'Settings' : 'Preferences & Settings'}
            />
          </MentorTabs>
        </Box>
      </Box>

      {/* Profile Dropdown Menu - Outside HeaderContainer (uses portal) */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderLeft: '1px solid rgba(16, 185, 129, 0.1)',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Section */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            backgroundColor: 'rgba(16, 185, 129, 0.02)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'rgba(15, 23, 42, 0.95)',
              fontSize: '14px',
              mb: 0.5,
            }}
          >
            {mentorProfileData?.name || 'Doctor'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(100, 116, 139, 0.8)',
              fontSize: '12px',
            }}
          >
            Mentor Portal
          </Typography>
        </Box>

        {/* Profile Menu Item */}
        <MenuItem
          onClick={() => {
            navigate('/mentor-home/profile')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(71, 85, 105, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              color: 'rgba(16, 185, 129, 0.95)',
            },
          }}
        >
          <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(16, 185, 129, 0.7)' }} />
          Profile Settings
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Logout */}
        <MenuItem
          onClick={() => {
            logoutHandlerMentor()
            navigate('/')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(239, 68, 68, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              color: 'rgba(239, 68, 68, 1)',
            },
          }}
        >
          <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
          Sign Out
        </MenuItem>
      </Menu>
      <HomePageContainer>
        {/* EMR Checkin - Bookings tile - navigate to my-clinic-associations */}
        {mentorClinicAssociationData && mentorClinicAssociationData?.length > 0 && (
          <HomePageItem
            otherColor="linear-gradient(46deg, #97271f 0%, #ff5f35 100%)"
            onClick={() => navigate('/mentor-home/my-clinic-associations')}
          >
            {translatedStrings?.clinic_bookings || 'EMR Checkin - Bookings'}
          </HomePageItem>
        )}

        <HomePageItem
          otherColor="linear-gradient(46deg, #97271f 0%, #ff5f35 100%)"
          onClick={() => navigate('/mentor-home/upcoming-appointments')}
        >
          {'Upcoming Appointments'}
        </HomePageItem>

        {getAllRolesInBoolean().isDoctor && mentorProfileData?.allowedToPrescribe && (
          <HomePageItem
            otherColor="linear-gradient(46deg, #223948 0%, #4639a2 100%)"
            onClick={() => navigate('/mentor-home/prescription-templates')}
          >
            {translatedStrings?.prescription_templates || 'Prescription Pads'}
          </HomePageItem>
        )}

        {offersLive && (
          <HomePageItem onClick={() => navigate('/mentor-home/my-calendar')}>
            {translatedStrings?.booked_sessions_and_calendar_settings ||
              'Booked Sessions and Calendar Settings'}
          </HomePageItem>
        )}

        <HomePageItem
          otherColor="linear-gradient(46deg, #000 0%, #00FFB2 100%)"
          onClick={() => navigate('/mentor-home/profile')}
        >
          {translatedStrings?.my_profile || 'My Profile'}
        </HomePageItem>

        {mentorProfileData.services.recorded &&
          mentorProfileData.contentCategories.includes('activity-plan') && (
            <HomePageItem
              otherColor="linear-gradient(46deg, #222848 0%, #4639a2 100%)"
              onClick={() => navigate('/mentor-home/homework-plans/')}
            >
              {translatedStrings?.faqs || 'Homework Plans'}
            </HomePageItem>
          )}

        <HomePageItem
          otherColor="linear-gradient(46deg, #749da6 0%, #4EC3C4 100%)"
          onClick={() => navigate('/mentor-home/settings')}
        >
          {translatedStrings?.emr_settings || 'EMR Settings'}
        </HomePageItem>

        <HomePageItem
          otherColor="linear-gradient(46deg, #4f340d 0%, #B88B4A 100%)"
          onClick={() => navigate('/mentor-home/faqs')}
        >
          {translatedStrings?.faqs || 'FAQs'}
        </HomePageItem>

        {allLegalPolicies?.length > 0 && (
          <HomePageItem
            otherColor="linear-gradient(46deg, #000 0%, #939393 100%)"
            onClick={() => navigate('/mentor-home/agreements')}
          >
            {translatedStrings?.agreements_tax_and_banking || 'Agreements, Tax and Banking'}
          </HomePageItem>
        )}
        {getAllRolesInBoolean().isTeacher && !!showPayoutReports && (
          <HomePageItem
            otherColor="linear-gradient(46deg,rgb(22, 35, 6) 0%, #41ac51 100%)"
            onClick={() => navigate('/mentor-home/payout-invoices')}
          >
            {'Your Payout Invoices'}
          </HomePageItem>
        )}
        {getAllRolesInBoolean().isTeacher && !!showPayoutReports && (
          <HomePageItem
            otherColor="linear-gradient(46deg, #133320 0%, #41ac51 100%)"
            onClick={() => navigate('/mentor-home/payout-reports')}
          >
            {'Your Payout Reports'}
          </HomePageItem>
        )}

        {getAllRolesInBoolean().isTeacher && !!showEngagementModels && (
          <HomePageItem
            otherColor="linear-gradient(46deg, #133320 0%, #41ac51 100%)"
            onClick={() => navigate('/mentor-home/engagement-models')}
          >
            {'Engagement Models'}
          </HomePageItem>
        )}
      </HomePageContainer>
      <div style={{ padding: '32px 8px', width: '100%' }}>
        {getAllRolesInBoolean().isClinicOwner && <ClinicOwnerReport />}
      </div>
    </>
  )
}

